// @flow strict
import * as React from "react";
import {
  now,
  secondsToTarget,
  formatDateDistance,
  formatDistance,
} from "~common/utils";
import { TOTAL_SECONDS, EXTRA_SECONDS } from "~robin-hood/utils";
import type { UnixTime } from "~common/utils";
import type { Equipo, Agent } from "~robin-hood/redux/store";
import { GROUP_CREATOR } from "~robin-hood/redux/store";
import {
  FinalizarMision,
  BuyMeACoffee,
} from "~robin-hood/registered/pruebas/PruebaFinal";
import styles from "./Clock.module.css";
import modal from "~robin-hood/components/Modal.module.css";
import { Modal } from "react-overlays";
import button from "~robin-hood/components/Button.module.css";
import classnames from "~common/classnames";

type ClockProps = {
  equipo?: Equipo,
  agent?: Agent,
  updateEquipo: (Equipo) => void,
  exitEquipo: () => void,
};

type ClockState = {
  running: boolean,
  tick?: number,
  showExtraModal: boolean,
  showEndModal: boolean,
};

function initialState(props: ClockProps) {
  const seconds =
    props.equipo &&
    props.equipo.extraTimeStart &&
    secondsToTarget(props.equipo.extraTimeStart, EXTRA_SECONDS);
  return {
    running: false,
    showExtraModal: false,
    showEndModal: seconds == null ? false : seconds < 0,
  };
}

export class Clock extends React.Component<ClockProps, ClockState> {
  state: ClockState = initialState(this.props);
  _timeout: ?TimeoutID;
  _mounted: boolean = false;
  componentDidMount() {
    this._mounted = true;
    if (this.props.equipo?.startTime || this.props.equipo?.extraTimeStart) {
      this.setState({ running: true });
      this._tick();
    } else {
      this.setState({ running: false });
    }
  }

  componentDidUpdate(prevProps: ClockProps) {
    if (
      prevProps.equipo?.startTime !== this.props.equipo?.startTime ||
      prevProps.equipo?.extraTimeStart !== this.props.equipo?.extraTimeStart
    ) {
      this.setState({ running: true });
      this._tick();
    }
  }
  componentWillUnmount() {
    this._mounted = false;
  }

  _tick = () => {
    if (this._timeout) return;
    this._timeout = setTimeout(() => {
      const { equipo } = this.props;
      if (!this._mounted || equipo?.endTime != null) {
        this._timeout = undefined;
        return;
      }
      if (equipo != null && equipo.startTime != null) {
        if (equipo.extraTimeStart == null) {
          const totalSecondsToTarget = secondsToTarget(
            equipo.startTime,
            TOTAL_SECONDS
          );
          if (totalSecondsToTarget < 0) {
            // Clock went to 0
            // We will give them some more minutes.
            this.setState({ showExtraModal: true });
            // We don't tick again
            this._timeout = undefined;
            return;
          }
        } else {
          const totalSecondsToTarget = secondsToTarget(
            equipo.extraTimeStart,
            EXTRA_SECONDS
          );
          if (totalSecondsToTarget < 0) {
            // Clock went to 0
            // We will give them some more minutes.
            this.setState({ showEndModal: true });
            // We don't tick again
            this._timeout = undefined;
            return;
          }
        }
      }
      this.setState((state) => ({ tick: (state.tick || 0 + 1) % 2 }));
      this._timeout = undefined;
      this._tick();
    }, 200);
  };

  _onClick = () => {
    const { equipo, agent, updateEquipo } = this.props;
    const { showExtraModal, showEndModal } = this.state;
    if (
      showEndModal ||
      showExtraModal ||
      !equipo ||
      !agent ||
      agent !== GROUP_CREATOR ||
      !updateEquipo
    ) {
      return;
    }
    const { running } = this.state;
    if (!running) {
      const initial = now();
      this.setState({ running: true });
      updateEquipo(updateInitialTime(equipo, initial));
      this._tick();
    } else {
      // this.setState({ running: false, tick: undefined });
      // updateEquipo(resetInitialTime(equipo));
      // if (this._timeout) {
      //   this._timeout = clearTimeout(this._timeout);
      // }
    }
  };

  render(): React$Node {
    const { equipo, agent, updateEquipo } = this.props;
    const { showExtraModal, showEndModal } = this.state;
    let time;
    if (!equipo || !agent || !equipo.startTime) {
      return null;
    } else if (equipo.extraTimeStart != null) {
      if (equipo.endTime) {
        time = formatDistance(
          equipo.extraTimeStart,
          equipo.endTime,
          EXTRA_SECONDS
        );
      } else {
        time = formatDateDistance(equipo.extraTimeStart, EXTRA_SECONDS);
      }
    } else {
      if (equipo.endTime) {
        time = formatDistance(equipo.startTime, equipo.endTime, TOTAL_SECONDS);
      } else {
        time = formatDateDistance(equipo.startTime, TOTAL_SECONDS);
      }
    }
    return (
      <div
        className={classnames(
          styles.clock,
          equipo && agent === GROUP_CREATOR && styles.startTime,
          equipo?.extraTimeStart != null && styles.extraTime
        )}
        onClick={this._onClick}
      >
        <span>{time}</span>
        {equipo != null &&
          equipo.endTime == null &&
          equipo.extraTimeStart == null && (
            <MoreTimeModal
              showExtraModal={showExtraModal}
              onAccept={() => {
                if (equipo && updateEquipo) {
                  updateEquipo({
                    ...equipo,
                    extraTimeStart: now(),
                  });
                }
              }}
            />
          )}
        {equipo != null &&
          equipo.endTime == null &&
          equipo.extraTimeStart != null && (
            <EndTimeModal
              showEndModal={showEndModal}
              onAccept={() => {
                this.props.exitEquipo();
              }}
            />
          )}
      </div>
    );
  }
}
function updateInitialTime(equipo: Equipo, startTime: UnixTime): Equipo {
  return { ...equipo, startTime };
}

type ModalProps = {
  onAccept: () => void,
  showExtraModal: boolean,
};

function MoreTimeModal(props: ModalProps) {
  return (
    <Modal
      show={props.showExtraModal}
      backdrop={"static"}
      onHide={() => {
        // Noop, we want to close it ourselves
      }}
      renderBackdrop={(props) => (
        <div className={modal.backdrop} {...props}></div>
      )}
    >
      <div className={modal.modal}>
        <div className={modal.modalContent}>
          <h1 className={classnames(modal.modalTitle, modal.center)}>
            ¡Cuidado!
          </h1>
          <p>
            ¡Los dueños de las cuentas han estado a punto de pillaros! Por
            suerte, hemos conseguido evitar que os detecten ahora mismo, pero
            sólo podremos distraerlos 10 minutos más.
          </p>
          <p>¡Dáos prisa para encontrar la solución y abrir la caja fuerte!</p>
          <div className={modal.modalSubmit}>
            <span
              className={classnames(button.button, button.alt)}
              onClick={props.onAccept}
            >
              Continuar
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
}
type EndModalProps = {
  showEndModal: boolean,
  onAccept: () => void,
};

function EndTimeModal(props: EndModalProps) {
  return (
    <Modal
      show={props.showEndModal}
      backdrop={"static"}
      onHide={() => {
        // Noop, we want to close it ourselves
      }}
      renderBackdrop={(props) => (
        <div className={modal.backdrop} {...props}></div>
      )}
    >
      <div className={modal.modal}>
        <div className={modal.modalContent}>
          <h1 className={modal.modalTitle}>¡Oh no, se ha acabado el tiempo!</h1>
          <p>
            No hemos conseguido acceder al depósito del dinero robado así que
            tendremos que intertarlo de nuevo en otra ocasión.
          </p>
          <p>
            Afortunadamente, hemos conseguido ocultar vuestra identidad, así que
            no debéis preocuparos por futuras represalias.
          </p>
          <p>
            Si os a gustado esta aventura y queréis darnos alguna idea o estar
            informados de nuevas misiones, sólo tenéis que seguirnos en nuestra
            página de{" "}
            <a
              className={styles.google}
              target="_blank"
              rel="noopener noreferrer"
              href="https://g.page/el-gato-encerrado-escape-rooms-a/"
            >
              <b>Google</b>
            </a>
            o{" "}
            <a
              className={styles.facebook}
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.facebook.com/elgatoencerrado.escaperoom.sevilla/"
            >
              <b>Facebook</b>
            </a>{" "}
            , o apoyar nuestro trabajo invitándonos a un café.
          </p>
          <div className={modal.modalSubmit}>
            <BuyMeACoffee />
            <FinalizarMision />
          </div>
        </div>
      </div>
    </Modal>
  );
}
